import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { syncTwilioNumbersEdge } from "@/integrations/supabase/twilioApi";
interface Subaccount {
  sid: string;
  friendly_name: string;
  status: string;
  date_created: string;
}

interface Tenant { tenant_id: string; name: string; twilio_sid: string | null; }
interface PhoneNumber { sid: string; phone_number: string; friendly_name?: string; country_code?: string; }

const fetchSubaccounts = async (): Promise<Subaccount[]> => {
  const { data, error } = await supabase.functions.invoke("Calie_listSubaccounts", { body: {} });
  if (error) throw error;
  return data?.subaccounts || [];
};

const fetchTenants = async (): Promise<Tenant[]> => {
  const { data, error } = await supabase.functions.invoke("Calie_adminListTenants", { body: {} });
  if (error) throw error;
  return data?.tenants || [];
};

const updateSubaccount = async (payload: { subaccountSid: string; friendlyName?: string; status?: string; }) => {
  const { data, error } = await supabase.functions.invoke("Calie_updateSubaccount", { body: payload });
  if (error) throw error;
  return data;
};

const createSubaccountForTenant = async (tenant_id: string) => {
  const { data, error } = await supabase.functions.invoke("Calie_create_subaccount", { body: { tenant_id } });
  if (error) throw error;
  return data;
};

const TwilioSubaccountsPage: React.FC = () => {
  // SEO
  useEffect(() => {
    document.title = "Twilio Subaccounts Admin | Calie.ai";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Admin dashboard to manage Twilio subaccounts in Calie.ai");
  }, []);

  const { hasRole, loading } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const isAdmin = hasRole?.("admin");
  const canQuery = !loading && isAdmin === true;

  const { data: subaccounts, isLoading } = useQuery({ 
    queryKey: ["twilio-subaccounts"], 
    queryFn: fetchSubaccounts,
    enabled: canQuery,
  });
  const { data: tenants } = useQuery({ 
    queryKey: ["tenants"], 
    queryFn: fetchTenants,
    enabled: canQuery,
  });

  const [selectedTenant, setSelectedTenant] = useState<string>("");
  const [newName, setNewName] = useState<string>("");
  const [linkTenantId, setLinkTenantId] = useState<string>("");
  const [linkSubaccountSid, setLinkSubaccountSid] = useState<string>("");
  const [unlinkTenantId, setUnlinkTenantId] = useState<string>("");
  const [numbersOpen, setNumbersOpen] = useState(false);
  const [numbersLoading, setNumbersLoading] = useState(false);
  const [numbers, setNumbers] = useState<PhoneNumber[]>([]);
  const [selectedSubSid, setSelectedSubSid] = useState<string>("");
  const [allNumbersLoading, setAllNumbersLoading] = useState(false);
  const [allNumbers, setAllNumbers] = useState<Array<PhoneNumber & { subaccount_sid: string; subaccount_name?: string }>>([]);
  const mutateUpdate = useMutation({
    mutationFn: updateSubaccount,
    onSuccess: () => {
      toast({ title: "Subaccount updated" });
      qc.invalidateQueries({ queryKey: ["twilio-subaccounts"] });
    },
    onError: (e: any) => toast({ title: "Update failed", description: e?.message || "", variant: "destructive" })
  });

  const mutateCreate = useMutation({
    mutationFn: createSubaccountForTenant,
    onSuccess: (res) => {
      toast({ title: "Subaccount created", description: res?.account?.sid || "" });
      qc.invalidateQueries({ queryKey: ["twilio-subaccounts" ]});
    },
    onError: (e: any) => toast({ title: "Creation failed", description: e?.message || "", variant: "destructive" })
  });

  const mutateLink = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("Calie_linkSubaccount", {
        body: { tenantId: linkTenantId, subaccountSid: linkSubaccountSid }
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Linking failed");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Tenant linked to subaccount" });
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (e: any) => toast({ title: "Linking failed", description: e?.message || "", variant: "destructive" })
  });

  const mutateUnlink = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("Calie_unlinkSubaccount", {
        body: { tenantId: unlinkTenantId }
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Unlink failed");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Tenant unlinked" });
      setUnlinkTenantId("");
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (e: any) => toast({ title: "Unlink failed", description: e?.message || "", variant: "destructive" })
  });

  const loadNumbers = async (subSid?: string) => {
    setNumbersLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("Calie_adminListNumbers", {
        body: { subaccountSid: subSid || selectedSubSid }
      });
      if (error) throw error;
      setNumbers(data?.numbers || []);
    } catch (e: any) {
      toast({ title: "Failed to load numbers", description: e?.message || "", variant: "destructive" });
    } finally {
      setNumbersLoading(false);
    }
  };

  const loadAllNumbers = async () => {
    if (!subaccounts || subaccounts.length === 0) {
      toast({ title: "No subaccounts", description: "There are no subaccounts to load numbers from.", variant: "destructive" });
      return;
    }
    setAllNumbersLoading(true);
    try {
      const results: Array<PhoneNumber & { subaccount_sid: string; subaccount_name?: string }> = [];
      for (const sa of subaccounts) {
        try {
          const { data, error } = await supabase.functions.invoke("Calie_adminListNumbers", {
            body: { subaccountSid: sa.sid }
          });
          if (error) throw error;
          const nums: PhoneNumber[] = data?.numbers || [];
          for (const n of nums) {
            results.push({ ...n, subaccount_sid: sa.sid, subaccount_name: sa.friendly_name });
          }
        } catch (err: any) {
          console.warn("Failed to load numbers for", sa.sid, err?.message);
        }
      }
      setAllNumbers(results);
    } catch (e: any) {
      toast({ title: "Failed to load all numbers", description: e?.message || "", variant: "destructive" });
    } finally {
      setAllNumbersLoading(false);
    }
  };

  const openNumbersModal = async (subSid: string) => {
    setSelectedSubSid(subSid);
    setNumbersOpen(true);
    await loadNumbers(subSid);
  };

  const onSyncNumbers = async () => {
    const tenant = (tenants || []).find(t => t.twilio_sid === selectedSubSid);
    if (!tenant) {
      toast({ title: "No linked tenant", description: "Link a tenant to sync numbers for this subaccount.", variant: "destructive" });
      return;
    }
    try {
      setNumbersLoading(true);
      await syncTwilioNumbersEdge(tenant.tenant_id, selectedSubSid);
      toast({ title: "Synced from Twilio" });
      await loadNumbers();
    } catch (e: any) {
      toast({ title: "Sync failed", description: e?.message || "", variant: "destructive" });
    } finally {
      setNumbersLoading(false);
    }
  };

  const tenantOptions = useMemo(() => (tenants || []).map(t => ({ value: t.tenant_id, label: `${t.name} ${t.twilio_sid ? `(linked: ${t.twilio_sid})` : ""}` })), [tenants]);
  const linkedTenant = useMemo(() => (tenants || []).find(t => t.twilio_sid === selectedSubSid) || null, [tenants, selectedSubSid]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p>Checking permissions...</p>
        </div>
      </div>
    );
  }
  if (isAdmin === false) return <Navigate to="/" replace />;

  return (
    <TooltipProvider>
      <main className="p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Twilio Subaccounts Admin</h1>
        <p className="text-muted-foreground">Create, view, update, and close subaccounts.</p>
        <link rel="canonical" href={window.location.origin + "/admin/twilio-subaccounts"} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org", "@type": "WebPage", name: "Twilio Subaccounts Admin", description: "Admin dashboard to manage Twilio subaccounts in Calie.ai"
        }) }} />
      </header>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Create for tenant</h2>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <Select onValueChange={setSelectedTenant}>
            <SelectTrigger className="w-full md:w-80">
              <SelectValue placeholder="Select tenant" />
            </SelectTrigger>
            <SelectContent>
              {tenantOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button disabled={!selectedTenant || mutateCreate.isPending} onClick={() => selectedTenant && mutateCreate.mutate(selectedTenant)}>
            {mutateCreate.isPending ? "Creating..." : "Create subaccount"}
          </Button>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Link tenant to existing subaccount</h2>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <Select onValueChange={setLinkTenantId}>
            <SelectTrigger className="w-full md:w-80">
              <SelectValue placeholder="Select tenant" />
            </SelectTrigger>
            <SelectContent>
              {tenantOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select onValueChange={setLinkSubaccountSid}>
            <SelectTrigger className="w-full md:w-80">
              <SelectValue placeholder="Select subaccount" />
            </SelectTrigger>
            <SelectContent>
              {(subaccounts || []).map(sa => (
                <SelectItem key={sa.sid} value={sa.sid}>
                  {sa.sid} {sa.friendly_name ? `— ${sa.friendly_name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            disabled={!linkTenantId || !linkSubaccountSid || mutateLink.isPending}
            onClick={() => mutateLink.mutate()}
          >
            {mutateLink.isPending ? "Linking..." : "Link"}
          </Button>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Unlink tenant from subaccount</h2>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <Select onValueChange={setUnlinkTenantId}>
            <SelectTrigger className="w-full md:w-80">
              <SelectValue placeholder="Select linked tenant" />
            </SelectTrigger>
            <SelectContent>
              {(tenants || []).filter(t => !!t.twilio_sid).map(t => (
                <SelectItem key={t.tenant_id} value={t.tenant_id}>
                  {t.name} — {t.twilio_sid}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            disabled={!unlinkTenantId || mutateUnlink.isPending}
            onClick={() => mutateUnlink.mutate()}
          >
            {mutateUnlink.isPending ? "Unlinking..." : "Unlink tenant"}
          </Button>
        </div>
        <p className="text-muted-foreground text-sm">Unlinks the tenant record from its Twilio Subaccount without changing anything in Twilio.</p>
      </section>

      <section>

        <div className="flex items-center gap-2 mb-3">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New friendly name (for Rename action)" className="w-full md:w-96" />
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={5}>Loading...</TableCell></TableRow>
              ) : (subaccounts || []).map(sa => (
                <TableRow key={sa.sid}>
                  <TableCell className="font-mono text-xs">{sa.sid}</TableCell>
                  <TableCell>{sa.friendly_name || "—"}</TableCell>
                  <TableCell>{sa.status}</TableCell>
                  <TableCell>{new Date(sa.date_created).toLocaleString()}</TableCell>
                  <TableCell className="space-x-2">
                    <Button variant="outline" size="sm" onClick={() => openNumbersModal(sa.sid)}>View numbers</Button>
                    <Button variant="outline" size="sm" onClick={() => mutateUpdate.mutate({ subaccountSid: sa.sid, friendlyName: newName })} disabled={!newName || mutateUpdate.isPending}>Rename</Button>
                    {sa.status !== "active" && (
                      <Button size="sm" onClick={() => mutateUpdate.mutate({ subaccountSid: sa.sid, status: "active" })}>Activate</Button>
                    )}
                    {sa.status !== "suspended" && sa.status !== "closed" && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              if (confirm("Suspend this subaccount? This pauses usage and can be reactivated later.")) {
                                mutateUpdate.mutate({ subaccountSid: sa.sid, status: "suspended" });
                              }
                            }}
                          >
                            Suspend
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Temporarily disables calls and messaging. Reversible.</TooltipContent>
                      </Tooltip>
                    )}
                    {sa.status !== "closed" && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              if (confirm("Close this subaccount? This cannot be undone and may release numbers.")) {
                                mutateUpdate.mutate({ subaccountSid: sa.sid, status: "closed" });
                              }
                            }}
                          >
                            Close
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Irreversible. Deactivates the subaccount.</TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">All purchased numbers</h2>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={loadAllNumbers} disabled={allNumbersLoading || (subaccounts || []).length === 0}>
            {allNumbersLoading ? "Loading..." : "Load all numbers"}
          </Button>
          {(subaccounts || []).length === 0 && (
            <span className="text-muted-foreground text-xs">No subaccounts loaded</span>
          )}
        </div>
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subaccount</TableHead>
                <TableHead>Number</TableHead>
                <TableHead>Friendly name</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>SID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allNumbers.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center">{allNumbersLoading ? "Loading..." : "No numbers loaded yet"}</TableCell></TableRow>
              ) : allNumbers.map((n) => (
                <TableRow key={`${n.sid}-${n.subaccount_sid}`}>
                  <TableCell className="font-mono text-xs">{n.subaccount_name ? `${n.subaccount_name} (${n.subaccount_sid})` : n.subaccount_sid}</TableCell>
                  <TableCell className="font-mono">{n.phone_number}</TableCell>
                  <TableCell>{n.friendly_name || "—"}</TableCell>
                  <TableCell>{n.country_code || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{n.sid}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Purchased numbers</h2>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <Select value={selectedSubSid} onValueChange={(v) => { setSelectedSubSid(v); loadNumbers(v); }}>
            <SelectTrigger className="w-full md:w-96">
              <SelectValue placeholder="Select a subaccount" />
            </SelectTrigger>
            <SelectContent>
              {(subaccounts || []).map(sa => (
                <SelectItem key={sa.sid} value={sa.sid}>
                  {sa.sid} {sa.friendly_name ? `— ${sa.friendly_name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button size="sm" onClick={() => loadNumbers()} disabled={numbersLoading || !selectedSubSid}>
            {numbersLoading ? "Loading..." : "Refresh"}
          </Button>
          <Button size="sm" variant="secondary" onClick={onSyncNumbers} disabled={numbersLoading || !linkedTenant || !selectedSubSid}>
            {numbersLoading ? "Working..." : "Sync from Twilio"}
          </Button>
          {!linkedTenant && selectedSubSid && (
            <span className="text-muted-foreground text-xs">Link a tenant to enable sync</span>
          )}
        </div>

        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Friendly name</TableHead>
                <TableHead>SID</TableHead>
                <TableHead>Country</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!selectedSubSid ? (
                <TableRow><TableCell colSpan={4} className="text-center">Select a subaccount to view numbers</TableCell></TableRow>
              ) : numbers.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center">{numbersLoading ? "Loading..." : "No numbers found"}</TableCell></TableRow>
              ) : numbers.map((n) => (
                <TableRow key={n.sid}>
                  <TableCell className="font-mono">{n.phone_number}</TableCell>
                  <TableCell>{n.friendly_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{n.sid}</TableCell>
                  <TableCell>{n.country_code || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
      </main>
      <Dialog open={numbersOpen} onOpenChange={setNumbersOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Numbers for {selectedSubSid || "subaccount"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => loadNumbers()} disabled={numbersLoading}>{numbersLoading ? "Loading..." : "Refresh"}</Button>
              <Button size="sm" variant="secondary" onClick={onSyncNumbers} disabled={numbersLoading || !linkedTenant}>
                {numbersLoading ? "Working..." : "Sync from Twilio"}
              </Button>
              {!linkedTenant && <span className="text-muted-foreground text-xs">Link a tenant to enable sync</span>}
            </div>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Number</TableHead>
                    <TableHead>Friendly name</TableHead>
                    <TableHead>SID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {numbers.length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center">{numbersLoading ? "Loading..." : "No numbers found"}</TableCell></TableRow>
                  ) : numbers.map((n) => (
                    <TableRow key={n.sid}>
                      <TableCell className="font-mono">{n.phone_number}</TableCell>
                      <TableCell>{n.friendly_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{n.sid}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default TwilioSubaccountsPage;
